// COMPONENTS & FUNCTIONS
import { calculatePumpSizing } from "../calculations/pumpSizing";

// DATA
import { Acceleration } from "../baseQuantities/acceleration";
import { Density } from "../baseQuantities/density";
import { Flow } from "../baseQuantities/flow";
import { Length } from "../baseQuantities/length";
import { Pressure } from "../baseQuantities/pressure";
import { SurfaceFinish } from "../baseQuantities/surfaceFinish";
import { UnitLess } from "../baseQuantities/unitLess";
import { Velocity } from "../baseQuantities/velocity";
import { Viscosity } from "../baseQuantities/viscosity";

// SVG & IMAGES
import pumpSizingFigure from "../../img/calc-figure-pump-sizing.svg";

// INTERFACES
import {
  AdvancedCalculator,
  AppData,
} from "../../interface/functionalityInterface";

/*
PUMP SIZING CALCULATOR INFORMATION
  Inputs and outputs are stored in coherent SI units before calculation.
  The model calculates the pump pressure increase and head between two vessels
  from static pressure, elevation, Darcy-Weisbach pipe losses, local K losses
  and valve Kv losses. Available NPSH is calculated on the suction side.
*/

const calculate = (inputs: Record<string, number>) =>
  calculatePumpSizing({
    density: inputs.density,
    viscosity: inputs.dynamic_viscosity,
    gravity: inputs.gravity,
    vaporPressure: inputs.vapor_pressure,
    flow: inputs.design_flow,
    suctionGaugePressure: inputs.suction_gauge_pressure,
    dischargeGaugePressure: inputs.discharge_gauge_pressure,
    suctionStaticHead: inputs.suction_liquid_level,
    dischargeStaticHead: inputs.discharge_liquid_level,
    suctionPipeLength: inputs.suction_pipe_length,
    suctionOuterDiameter: inputs.suction_outer_diameter,
    suctionWallThickness: inputs.suction_wall_thickness,
    suctionRoughness: inputs.suction_roughness,
    suctionLossCoefficient: inputs.suction_loss_coefficient,
    suctionKv: inputs.suction_valve_kv,
    suctionKvCount: inputs.suction_valve_count,
    dischargePipeLength: inputs.discharge_pipe_length,
    dischargeOuterDiameter: inputs.discharge_outer_diameter,
    dischargeWallThickness: inputs.discharge_wall_thickness,
    dischargeRoughness: inputs.discharge_roughness,
    dischargeLossCoefficient: inputs.discharge_loss_coefficient,
    dischargeKv: inputs.discharge_valve_kv,
    dischargeKvCount: inputs.discharge_valve_count,
    requiredNpsh: inputs.required_npsh,
  });

export const pumpSizingCalc: AdvancedCalculator = {
  type: AppData.ADVANCED_CALCULATOR,
  definitionId: "pump-sizing",
  updatedAt: "2026-07-27",
  name: "pump sizing",
  branch: "Fluids",
  difficulty: "complex",
  equation:
    "Δpₚ = p₂ − p₁ + ρg(z₂ − z₁) + Σ(Δp friction + Δp local)",
  figure: pumpSizingFigure,
  inputs: [
    {
      ...Density,
      id: "pump-density",
      name: "density",
      type: "numeric",
      value: "998",
      clearValue: "998",
      section: "fluid properties",
    },
    {
      ...Viscosity,
      id: "pump-viscosity",
      name: "dynamic_viscosity",
      type: "numeric",
      value: "0.001",
      clearValue: "0.001",
      section: "fluid properties",
    },
    {
      ...Acceleration,
      id: "pump-gravity",
      name: "gravity",
      value: "9.81",
      clearValue: "9.81",
      symbol: "g",
      section: "fluid properties",
    },
    {
      ...Pressure,
      id: "pump-vapor-pressure",
      name: "vapor_pressure",
      value: "12500",
      clearValue: "0",
      symbol: "pᵥ",
      section: "fluid properties",
    },
    {
      ...Length,
      id: "pump-required-npsh",
      name: "required_npsh",
      type: "numeric",
      value: "4",
      clearValue: "0",
      symbol: "NPSHᵣ",
      section: "fluid properties",
    },
    {
      ...Flow,
      id: "pump-design-flow",
      name: "design_flow",
      value: "5",
      clearValue: "",
      symbol: "Q",
      section: "system duty",
    },
    {
      ...Pressure,
      id: "pump-suction-pressure",
      name: "suction_gauge_pressure",
      value: "0",
      clearValue: "0",
      symbol: "p₁,g",
      selectedUnit: "bar",
      section: "system duty",
    },
    {
      ...Pressure,
      id: "pump-discharge-pressure",
      name: "discharge_gauge_pressure",
      value: "0",
      clearValue: "0",
      symbol: "p₂,g",
      selectedUnit: "bar",
      section: "system duty",
    },
    {
      ...Length,
      id: "pump-suction-level",
      name: "suction_liquid_level",
      type: "numeric",
      value: "2",
      clearValue: "0",
      symbol: "z₁",
      section: "system duty",
    },
    {
      ...Length,
      id: "pump-discharge-level",
      name: "discharge_liquid_level",
      type: "numeric",
      value: "6",
      clearValue: "0",
      symbol: "z₂",
      section: "system duty",
    },
    {
      ...Length,
      id: "pump-suction-length",
      name: "suction_pipe_length",
      type: "numeric",
      value: "2",
      clearValue: "",
      symbol: "L₁",
      section: "suction pipe",
    },
    {
      ...Length,
      id: "pump-suction-outer-diameter",
      name: "suction_outer_diameter",
      type: "numeric",
      value: "51",
      clearValue: "",
      symbol: "Dₒ,₁",
      selectedUnit: "mm",
      section: "suction pipe",
    },
    {
      ...Length,
      id: "pump-suction-wall-thickness",
      name: "suction_wall_thickness",
      type: "numeric",
      value: "1.2",
      clearValue: "",
      symbol: "t₁",
      selectedUnit: "mm",
      section: "suction pipe",
    },
    {
      ...SurfaceFinish,
      id: "pump-suction-roughness",
      name: "suction_roughness",
      type: "numeric",
      value: "0.05",
      clearValue: "",
      symbol: "ε₁",
      selectedUnit: "mm",
      section: "suction pipe",
    },
    {
      ...UnitLess,
      id: "pump-suction-loss-coefficient",
      name: "suction_loss_coefficient",
      value: "2.75",
      clearValue: "0",
      symbol: "ΣK₁",
      section: "suction pipe",
    },
    {
      ...Flow,
      id: "pump-suction-kv",
      name: "suction_valve_kv",
      value: "48",
      clearValue: "0",
      symbol: "Kv₁",
      section: "suction pipe",
    },
    {
      ...UnitLess,
      id: "pump-suction-kv-count",
      name: "suction_valve_count",
      value: "1",
      clearValue: "0",
      symbol: "n₁",
      section: "suction pipe",
    },
    {
      ...Length,
      id: "pump-discharge-length",
      name: "discharge_pipe_length",
      type: "numeric",
      value: "120",
      clearValue: "",
      symbol: "L₂",
      section: "discharge pipe",
    },
    {
      ...Length,
      id: "pump-discharge-outer-diameter",
      name: "discharge_outer_diameter",
      type: "numeric",
      value: "38",
      clearValue: "",
      symbol: "Dₒ,₂",
      selectedUnit: "mm",
      section: "discharge pipe",
    },
    {
      ...Length,
      id: "pump-discharge-wall-thickness",
      name: "discharge_wall_thickness",
      type: "numeric",
      value: "1.2",
      clearValue: "",
      symbol: "t₂",
      selectedUnit: "mm",
      section: "discharge pipe",
    },
    {
      ...SurfaceFinish,
      id: "pump-discharge-roughness",
      name: "discharge_roughness",
      type: "numeric",
      value: "0.05",
      clearValue: "",
      symbol: "ε₂",
      selectedUnit: "mm",
      section: "discharge pipe",
    },
    {
      ...UnitLess,
      id: "pump-discharge-loss-coefficient",
      name: "discharge_loss_coefficient",
      value: "8.25",
      clearValue: "0",
      symbol: "ΣK₂",
      section: "discharge pipe",
    },
    {
      ...Flow,
      id: "pump-discharge-kv",
      name: "discharge_valve_kv",
      value: "48",
      clearValue: "0",
      symbol: "Kv₂",
      section: "discharge pipe",
    },
    {
      ...UnitLess,
      id: "pump-discharge-kv-count",
      name: "discharge_valve_count",
      value: "4",
      clearValue: "0",
      symbol: "n₂",
      section: "discharge pipe",
    },
  ],
  outputs: [
    {
      ...Pressure,
      id: "pump-required-pressure",
      name: "required_pressure_increase",
      selectedUnit: "bar",
      decimalsOffset: 2,
      section: "pump duty",
    },
    {
      ...Length,
      id: "pump-required-head",
      name: "required_head",
      decimalsOffset: 2,
      section: "pump duty",
    },
    {
      ...Velocity,
      id: "pump-suction-velocity",
      name: "suction_velocity",
      decimalsOffset: 2,
      section: "suction results",
    },
    {
      ...UnitLess,
      id: "pump-suction-reynolds",
      name: "suction_reynolds_number",
      decimalsOffset: 0,
      section: "suction results",
    },
    {
      ...UnitLess,
      id: "pump-suction-friction-factor",
      name: "suction_friction_factor",
      decimalsOffset: 4,
      section: "suction results",
    },
    {
      ...Pressure,
      id: "pump-suction-local-loss",
      name: "suction_local_pressure_loss",
      selectedUnit: "bar",
      decimalsOffset: 3,
      section: "suction results",
    },
    {
      ...Pressure,
      id: "pump-suction-friction-loss",
      name: "suction_friction_pressure_loss",
      selectedUnit: "bar",
      decimalsOffset: 3,
      section: "suction results",
    },
    {
      ...Velocity,
      id: "pump-discharge-velocity",
      name: "discharge_velocity",
      decimalsOffset: 2,
      section: "discharge results",
    },
    {
      ...UnitLess,
      id: "pump-discharge-reynolds",
      name: "discharge_reynolds_number",
      decimalsOffset: 0,
      section: "discharge results",
    },
    {
      ...UnitLess,
      id: "pump-discharge-friction-factor",
      name: "discharge_friction_factor",
      decimalsOffset: 4,
      section: "discharge results",
    },
    {
      ...Pressure,
      id: "pump-discharge-local-loss",
      name: "discharge_local_pressure_loss",
      selectedUnit: "bar",
      decimalsOffset: 3,
      section: "discharge results",
    },
    {
      ...Pressure,
      id: "pump-discharge-friction-loss",
      name: "discharge_friction_pressure_loss",
      selectedUnit: "bar",
      decimalsOffset: 3,
      section: "discharge results",
    },
    {
      ...Length,
      id: "pump-available-npsh",
      name: "available_npsh",
      decimalsOffset: 2,
      section: "cavitation check",
    },
    {
      ...Length,
      id: "pump-npsh-margin",
      name: "npsh_margin",
      decimalsOffset: 2,
      section: "cavitation check",
    },
  ],
  calculate: (inputs) => {
    const result = calculate(inputs);
    if (!result) return null;

    const points = Array.from({ length: 101 }, (_, index) => {
      const flow = (inputs.design_flow * 2 * index) / 100;
      const point = calculate({ ...inputs, design_flow: flow });
      return {
        flow,
        required_pressure: point?.requiredPressureIncrease ?? NaN,
        required_head: point?.requiredHead ?? NaN,
      };
    });

    return {
      outputs: {
        required_pressure_increase: result.requiredPressureIncrease,
        required_head: result.requiredHead,
        suction_velocity: result.suction.velocity,
        suction_reynolds_number: result.suction.reynoldsNumber,
        suction_friction_factor: result.suction.frictionFactor,
        suction_local_pressure_loss: result.suction.localPressureLoss,
        suction_friction_pressure_loss:
          result.suction.frictionPressureLoss,
        discharge_velocity: result.discharge.velocity,
        discharge_reynolds_number: result.discharge.reynoldsNumber,
        discharge_friction_factor: result.discharge.frictionFactor,
        discharge_local_pressure_loss:
          result.discharge.localPressureLoss,
        discharge_friction_pressure_loss:
          result.discharge.frictionPressureLoss,
        available_npsh: result.availableNpsh,
        npsh_margin: result.npshMargin,
      },
      series: points,
    };
  },
  validate: (inputs) => {
    const messages: string[] = [];
    const result = calculate(inputs);

    if (
      inputs.suction_outer_diameter <=
      2 * inputs.suction_wall_thickness
    ) {
      messages.push(
        "The suction wall thickness leaves no open pipe diameter."
      );
    }
    if (
      inputs.discharge_outer_diameter <=
      2 * inputs.discharge_wall_thickness
    ) {
      messages.push(
        "The discharge wall thickness leaves no open pipe diameter."
      );
    }
    if (inputs.suction_valve_count > 0 && inputs.suction_valve_kv <= 0) {
      messages.push("Enter a suction valve Kv when its count is above zero.");
    }
    if (
      inputs.discharge_valve_count > 0 &&
      inputs.discharge_valve_kv <= 0
    ) {
      messages.push(
        "Enter a discharge valve Kv when its count is above zero."
      );
    }
    if (result && result.npshMargin < 0.5) {
      messages.push(
        "Cavitation risk: the NPSH margin is below the recommended 0.5 m."
      );
    }

    return messages;
  },
  chart: {
    title: "Pump duty over flow",
    xAxis: {
      key: "flow",
      label: "Flow",
      baseUnit: Flow.baseUnit,
      selectedUnit: Flow.selectedUnit,
      selectedUnitFromInput: "design_flow",
      units: Flow.units,
    },
    datasets: [
      {
        key: "required_pressure",
        label: "Required pressure increase",
        axisId: "pressure",
        baseUnit: Pressure.baseUnit,
        selectedUnit: "bar",
        units: Pressure.units,
        borderColor: "#001e1d",
      },
      {
        key: "required_head",
        label: "Required head",
        axisId: "head",
        baseUnit: Length.baseUnit,
        selectedUnit: Length.selectedUnit,
        units: Length.units,
        borderColor: "#777",
        borderDash: [5, 4],
      },
    ],
  },
};
